import type { SubWalletResponse } from "@developerz.ai/ggcoin-sdk";
import { A } from "@solidjs/router";
import { createSignal, For, onMount, Show } from "solid-js";
import { useAuth } from "../auth/AuthContext.tsx";
import { ErrorBanner } from "../components/ErrorBanner.tsx";
import { rememberCheckout } from "../lib/checkout.ts";
import { parseAmountInput, sanitizeAmountInput } from "../lib/format.ts";

/**
 * Closed-loop on-ramp: fiat in → coin credited to one of the user's sub-wallets
 * (#26). Coins never flow back out — a legal invariant.
 *
 * Buy starts a real hosted checkout (#150): we ask the api for a gateway
 * redirect, stash a marker, and send the browser to the payment page. The coin
 * is minted server-side only on the gateway's verified capture callback — there
 * is no client-initiated mint. The buyer lands back on /buy/complete, which
 * waits for the mint to appear.
 */
export function Buy() {
  const { client, ensureMaster } = useAuth();
  const [walletId, setWalletId] = createSignal<string | null>(null);
  const [subs, setSubs] = createSignal<SubWalletResponse[]>([]);
  const [subWalletId, setSubWalletId] = createSignal("");
  const [amount, setAmount] = createSignal("");
  const [loading, setLoading] = createSignal(true);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<unknown | null>(null);

  onMount(async () => {
    setLoading(true);
    setError(null);
    try {
      const master = await ensureMaster();
      setWalletId(master.id);
      const list = await client.listSubWallets(master.id);
      setSubs(list);
      setSubWalletId((prev) => prev || list[0]?.id || "");
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  });

  async function onBuy(e: Event) {
    e.preventDefault();
    setError(null);
    const parsed = parseAmountInput(amount());
    if (!parsed || BigInt(parsed) <= 0n) {
      setError(new Error("Enter an amount greater than zero (at most 2 decimals)."));
      return;
    }
    const wid = walletId();
    const sub = subWalletId();
    if (!wid || !sub) {
      setError(new Error("Pick a sub-wallet to credit."));
      return;
    }
    setBusy(true);
    try {
      const session = await client.startCheckout({
        walletId: wid,
        subWalletId: sub,
        amount: parsed,
      });
      // Remember what we're buying so /buy/complete can recognize the mint when
      // the gateway's callback posts it.
      rememberCheckout(sessionStorage, {
        subWalletId: sub,
        amount: parsed,
        startedAt: new Date().toISOString(),
      });
      // Hand the browser to the gateway's hosted payment page.
      window.location.assign(session.redirectUrl);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Show
      when={!loading()}
      fallback={
        <section class="card">
          <h2>Buy GG-Coin</h2>
          <p class="muted">Loading your wallet…</p>
        </section>
      }
    >
      <section class="card">
        <h2>Buy GG-Coin</h2>
        <p class="muted">
          Fiat in → coin credited to a sub-wallet. Closed-loop: coin never cashes out. You'll be
          taken to our payment provider; coin is credited once the payment clears.
        </p>
        <ErrorBanner error={error()} />
        <Show
          when={subs().length > 0}
          fallback={
            <p>
              You need a sub-wallet to receive coin first. <A href="/sub-wallets">Create one</A>,
              then come back.
            </p>
          }
        >
          <form onSubmit={onBuy}>
            <label for="sub">Credit sub-wallet</label>
            <select
              id="sub"
              value={subWalletId()}
              onChange={(e) => setSubWalletId(e.currentTarget.value)}
            >
              <For each={subs()}>
                {(s) => (
                  <option value={s.id}>
                    {s.label} ({s.id.slice(0, 8)}…)
                  </option>
                )}
              </For>
            </select>
            <label for="amount" style={{ "margin-top": "0.75rem" }}>
              Amount (GG)
            </label>
            <input
              id="amount"
              inputmode="decimal"
              value={amount()}
              onInput={(e) => setAmount(sanitizeAmountInput(e.currentTarget.value))}
              placeholder="25.00"
              required
            />
            <div class="row" style={{ "margin-top": "0.75rem" }}>
              <button type="submit" class="primary" disabled={busy()}>
                {busy() ? "Redirecting…" : "Continue to payment"}
              </button>
            </div>
          </form>
        </Show>
      </section>
    </Show>
  );
}
